import { PushNotifications } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import { api } from '../lib/axios';
import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { toast } from 'react-hot-toast';

let listenersAdded = false;
let webListenersAdded = false;

const showPremiumToast = (title: string, body: string) => {
  toast.custom((t) => (
    <div 
      className={`${
        t.visible ? 'animate-enter' : 'animate-leave'
      } max-w-sm w-full bg-white shadow-xl rounded-2xl pointer-events-auto flex items-center justify-between p-4 border border-slate-100/80 ring-1 ring-black ring-opacity-5 transition-all duration-300`}
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="flex-shrink-0 bg-emerald-50 text-emerald-600 rounded-full p-2">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 0 1-1.043 3.296 3.745 3.745 0 0 1-3.296 1.043A3.745 3.745 0 0 1 12 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 0 1-3.296-1.043 3.745 3.745 0 0 1-1.043-3.296A3.745 3.745 0 0 1 3 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 0 1 1.043-3.296 3.746 3.746 0 0 1 3.296-1.043A3.746 3.746 0 0 1 12 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 0 1 3.296 1.043 3.746 3.746 0 0 1 1.043 3.296A3.745 3.745 0 0 1 21 12Z" />
          </svg>
        </div>
        <div className="flex-1 min-w-0 text-left">
          <p className="text-sm font-bold text-slate-800 truncate">{title}</p>
          <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{body}</p>
        </div>
      </div>
      <button 
        onClick={() => toast.dismiss(t.id)} 
        className="ml-4 flex-shrink-0 text-slate-400 hover:text-slate-600 p-1.5 hover:bg-slate-50 rounded-lg transition-colors border-none cursor-pointer focus:outline-none"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  ), { duration: 8000, position: 'top-right' });
};

export const initPushNotifications = async (): Promise<boolean> => {
  // 1. Handle Web Platform (PWA / Browser)
  if (Capacitor.getPlatform() === 'web') {
    try {
      const [configRes, vapidRes] = await Promise.all([
        api.get('/config/firebase?platform=web'),
        api.get('/config/firebase?platform=vapid')
      ]);

      const firebaseConfig = configRes.data;
      const vapidKey = vapidRes.data.rawConfig || vapidRes.data;

      const app = initializeApp(firebaseConfig);
      const messaging = getMessaging(app);

      const token = await getToken(messaging, { vapidKey });
      
      if (token) {
        // Determine explicit web platform
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
        const explicitPlatform = isIOS ? 'web-ios' : 'web-desktop';

        await api.post('/push/register', {
          token: token,
          platform: explicitPlatform
        });
        console.log(`Web token registered for ${explicitPlatform}`);

        // Handle foreground messages uniquely
        if (!webListenersAdded) {
          onMessage(messaging, (payload) => {
            console.log('Foreground message received:', payload);
            if (payload.notification) {
              showPremiumToast(
                payload.notification.title || 'Notification',
                payload.notification.body || ''
              );
            }
          });
          webListenersAdded = true;
          console.log('Web push foreground listener registered');
        }

        return true;
      }
      return false;
    } catch (err) {
      console.error('Web push registration failed:', err);
      return false;
    }
  }

  // 2. Handle Native Platforms
  return new Promise(async (resolve) => {
    try {
      console.log('PushNotifications: Checking permissions...');
      let permStatus = await PushNotifications.checkPermissions();
      
      if (permStatus.receive === 'prompt') {
        permStatus = await PushNotifications.requestPermissions();
      }

      if (permStatus.receive !== 'granted') {
        console.warn('PushNotifications: Permission not granted');
        resolve(false);
        return;
      }

      // SAFE REGISTRATION:
      // We wrap the native register call. If google-services.json is missing, 
      // this is where the native crash usually originates.
      console.log('PushNotifications: Attempting native registration...');
      try {
        await PushNotifications.register();
      } catch (regError) {
        console.error('PushNotifications: Native registration call failed. Is google-services.json missing?', regError);
        resolve(false);
        return;
      }

      // Handle foreground messages on native uniquely
      if (!listenersAdded) {
        await PushNotifications.addListener('pushNotificationReceived', (notification) => {
          console.log('Push notification received: ', notification);
          showPremiumToast(
            notification.title || 'Notification',
            notification.body || ''
          );
        });

        // Handle notification action
        await PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
          console.log('Push notification action performed', notification);
        });

        listenersAdded = true;
        console.log('Native push notification listeners registered');
      }

      const successListener = await PushNotifications.addListener('registration', async (token) => {
        console.log('PushNotifications: Registration successful, token received');
        try {
          await api.post('/push/register', {
            token: token.value,
            platform: Capacitor.getPlatform()
          });
          successListener.remove();
          resolve(true);
        } catch (err) {
          console.error('PushNotifications: Failed to send token to server', err);
          resolve(false);
        }
      });

      const errorListener = await PushNotifications.addListener('registrationError', (error) => {
        console.error('PushNotifications: Registration error event', error);
        errorListener.remove();
        resolve(false);
      });

      // Cleanup listeners after timeout if no response (Don't hang the app)
      setTimeout(() => {
        successListener.remove();
        errorListener.remove();
        console.warn('PushNotifications: Registration timed out');
        resolve(false);
      }, 5000); // Reduced to 5 seconds to be more responsive

    } catch (err) {
      console.error('PushNotifications: Critical failure in push service', err);
      resolve(false);
    }
  });
};
